import { ConfirmModal, CustomButton, CustomSelect } from '@/shared/ui/components';
import { getContent } from '@/entities/study';
import { inforBlackIcon } from '@/shared/ui/assets';
import { DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import { Drawer } from 'antd';
import dayjs from 'dayjs';
import dayjsBusinessDays from 'dayjs-business-days';
import utc from 'dayjs/plugin/utc';
import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { t } from 'ttag';
import { useEditInterPhysicianDrawer } from './useEditInterPhysicianDrawer';
import type { EditInterPhysicianProps } from '../editStudyDetails.types';

dayjs.extend(dayjsBusinessDays);
dayjs.extend(utc);

const EditInterPhysicianDrawer = ({
  data,
  open,
  interprettingPhysicians,
  onClose,
  drawerKey,
  updateStudyData,
  ...params
}: EditInterPhysicianProps) => {
  const {
    state,
    setState,
    isAbortedStudy,
    handleInterprettingPhysicianChange,
    handleClickSave,
    handleOnClose,
    onSave,
  } = useEditInterPhysicianDrawer({
    data,
    open,
    interprettingPhysicians,
    onClose,
    drawerKey,
    updateStudyData,
  });

  return (
    <Drawer
      {...params}
      open={open}
      rootClassName="edit-study-drawer-wrapper"
      title={t`Interpreting physician`}
      aria-label={t`Interpreting physician`}
      onClose={handleOnClose}
      destroyOnClose
      maskClosable={false}
    >
      {state.isInitiating ? (
        <div className="edit-study-details">
          <Skeleton className="initiating-skeleton" />
        </div>
      ) : (
        <>
          <ConfirmModal
            title={t`Apply changes`}
            question={getContent()}
            isOpen={state.isOpenConfirmModal}
            leftButtonName={t`Cancel`}
            rightButtonName={t`Apply changes`}
            onClickLeftButton={() => {
              setState({ isOpenConfirmModal: false });
            }}
            onClickRightButton={() => {
              onSave();
            }}
            isSaving={state.isSaving}
          />

          <div className="edit-study-details">
            <div className="content-container">
              <div className="content">
                <div className="left">
                  <div className="title">{t`Interpreting physician`}</div>
                </div>
                <div className="right">
                  <div>
                    <CustomSelect
                      name="interprettingPhysician"
                      value={state.selectedInterprettingPhysician}
                      size="middle"
                      onChange={handleInterprettingPhysicianChange}
                      placeholder={t`Select interpreting physician`}
                      allowClear={false}
                      options={interprettingPhysicians}
                      disabled={isAbortedStudy}
                      labelInValue
                      id="select-interpreting-physician"
                      aria-label={t`Interpreting physician`}
                    />
                    {isAbortedStudy && (
                      <div className="abort-input">
                        <img src={inforBlackIcon} alt="inforBlackIcon" />
                        {t`Cannot edit interpreting physician of aborted study.`}
                      </div>
                    )}
                  </div>
                  <div className="referring-info">
                    <div className="name">{state.selectedInterprettingPhysician?.name}</div>
                    <div className="infor">
                      <div className="label">{`${t`Day phone`}:`}</div>
                      <div className="value">
                        {!_.isEmpty(state.selectedInterprettingPhysician)
                          ? state.selectedInterprettingPhysician.phone1 || DOUBLE_HYPHEN_BULLET
                          : data?.info?.interprettingPhysician?.contact?.phone1 ||
                            DOUBLE_HYPHEN_BULLET}
                      </div>
                      <div className="label">{`${t`After hours phone`}:`}</div>
                      <div className="value">
                        {!_.isEmpty(state.selectedInterprettingPhysician)
                          ? state.selectedInterprettingPhysician.nightPhone || DOUBLE_HYPHEN_BULLET
                          : data?.info?.interprettingPhysician?.contact?.nightPhone ||
                            DOUBLE_HYPHEN_BULLET}
                      </div>
                      <div className="label">{`${t`Email`}:`}</div>
                      <div className="value">
                        {!_.isEmpty(state.selectedInterprettingPhysician)
                          ? state.selectedInterprettingPhysician.email || DOUBLE_HYPHEN_BULLET
                          : data?.info?.interprettingPhysician?.email || DOUBLE_HYPHEN_BULLET}
                      </div>
                      <div className="label">{`${t`Fax number`}:`}</div>
                      <div className="value">
                        {!_.isEmpty(state.selectedInterprettingPhysician)
                          ? state.selectedInterprettingPhysician.fax || DOUBLE_HYPHEN_BULLET
                          : data?.info?.interprettingPhysician?.contact?.fax ||
                            DOUBLE_HYPHEN_BULLET}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="edit-study-button-container">
                <CustomButton
                  id="cancel-btn"
                  type="outline"
                  style="neutral"
                  size="middle"
                  onClick={handleOnClose}
                >
                  {t`Cancel`}
                </CustomButton>
                <CustomButton
                  id="save-btn"
                  type="primary"
                  style="default"
                  size="middle"
                  onClick={handleClickSave}
                >
                  {t`Save`}
                </CustomButton>
              </div>
            </div>
          </div>
        </>
      )}
    </Drawer>
  );
};

export default EditInterPhysicianDrawer;
